import type { ApiClient } from '@classgo/app-kit';
import type {
  CreateTopicInput,
  GradeSubjectView,
  PlanView,
  PutGradeSubjectsInput,
  ReorderTopicsInput,
  SeedPlanInput,
  SeedPreviewTopic,
  TopicView,
  UpdateTopicInput,
} from '@classgo/shared';
import { queryOptions } from '@tanstack/react-query';

/**
 * The plan de estudios endpoints (ticket #58): subjects per grade, the plan of a grade and
 * subject, topics, ordering and the DBA seed.
 *
 * @param {ApiClient} api -- The client.
 * @returns The calls.
 */
export const curriculumApi = (api: ApiClient) => ({
  putGradeSubjects: (gradeId: string, body: PutGradeSubjectsInput) =>
    api.request<GradeSubjectView[]>(`/curriculum/grades/${gradeId}/subjects`, {
      method: 'PUT',
      body,
    }),
  createTopic: (body: CreateTopicInput) =>
    api.request<TopicView>('/curriculum/topics', { method: 'POST', body }),
  updateTopic: (id: string, body: UpdateTopicInput) =>
    api.request<TopicView>(`/curriculum/topics/${id}`, { method: 'PATCH', body }),
  deleteTopic: (id: string) => api.request<void>(`/curriculum/topics/${id}`, { method: 'DELETE' }),
  reorder: (body: ReorderTopicsInput) =>
    api.request<PlanView>('/curriculum/plan/reorder', { method: 'POST', body }),
  seed: (body: SeedPlanInput) =>
    api.request<PlanView>('/curriculum/plan/seed', { method: 'POST', body }),
});

/**
 * Query options of the curriculum screens.
 */
export const curriculumQueries = {
  gradeSubjects: (api: ApiClient, gradeId: string) =>
    queryOptions({
      queryKey: ['curriculum', 'grade-subjects', gradeId],
      queryFn: () => api.request<GradeSubjectView[]>(`/curriculum/grades/${gradeId}/subjects`),
      enabled: gradeId !== '',
    }),
  plan: (api: ApiClient, gradeId: string, subjectId: string) =>
    queryOptions({
      queryKey: ['curriculum', 'plan', gradeId, subjectId],
      queryFn: () =>
        api.request<PlanView>(`/curriculum/plan?gradeId=${gradeId}&subjectId=${subjectId}`),
      enabled: gradeId !== '' && subjectId !== '',
    }),
  seedPreview: (api: ApiClient, gradeId: string, subjectId: string, enabled: boolean) =>
    queryOptions({
      queryKey: ['curriculum', 'seed-preview', gradeId, subjectId],
      queryFn: () =>
        api.request<{ items: SeedPreviewTopic[] } | SeedPreviewTopic[]>(
          `/curriculum/seed-preview?gradeId=${gradeId}&subjectId=${subjectId}`,
        ),
      enabled,
    }),
};
